import React, { useState } from "react";
import ConsultationForm from "./Consultation"; // adjust path if your pages folder is elsewhere
import OtSuit1 from "../assets/images/Ot-suit/Ot-suit-1.webp";
import OtSuit2 from "../assets/images/Ot-suit/Ot-suit-2.jpg";
import OtSuit3 from "../assets/images/Ot-suit/Ot-suit-3.jpg";
import OtSuit4 from "../assets/images/Ot-suit/Ot-suit-4.webp";
import OtSuit5 from "../assets/images/Ot-suit/Ot-suit-5.png";
import OtSuit6 from "../assets/images/Ot-suit/Ot-suit-6.jpeg";
import OtSuit7 from "../assets/images/Ot-suit/Ot-suit-7.webp";
import OtSuit8 from "../assets/images/Ot-suit/Ot-suit-8.png";
import OtSuit9 from "../assets/images/Ot-suit/Ot-suit-9.webp";
import SEO from "../components/SEO";

/**
 * Modular OT Suite Designs — landing page
 * Palette:  Surgical Steel #2B3A42 (ink), Sterile White #F6F8F8 (bg),
 *           Iodine Teal #0E7C7B (primary accent), Bio Amber #E1A33B (CTA/warn),
 *           Laminar Blue #335C81 (secondary), Hairline #D7E0E0
 * Type: display = "Fraunces" (clinical-but-warm serif, used sparingly for headings),
 *       body/UI = "IBM Plex Sans" (precision/instrumentation feel),
 *       data/labels = "IBM Plex Mono" (specs, sizes, codes)
 * Signature element: each suite "card" is framed like an OT control panel readout —
 *       a thin teal corner bracket + a mono spec-line (Zone Class / Size / ACH)
 *       instead of a generic price tag, because that's the real differentiator
 *       in this domain (not décor, but compliance & airflow class).
 */

const FONT_IMPORT_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";

const tokens = {
  ink: "#343F86",      // near-black, headings/dark panels
  bg: "#ECEEF6",        // off-white background
  card: "#FFFFFF",       // pure white card
  teal: "#3A4060",       // charcoal grey — replaces "accent" role
  tealDark: "#262E63",   // darker charcoal for gradients
  amber: "#8A8F9C",      // ash grey — replaces amber CTA accent
  blue: "#5B6072",        // mid grey — secondary gradient stop
  hairline: "#CDD1E0",   // light ash hairline/border
  sub: "#5B6072",         // grey body/secondary text
};

const suites = [
  {
    name: "Class 100 Laminar Flow Cardiac OT",
    zone: "ISO 5",
    size: "32 sq.m",
    ach: "20 ACH",
    tag: "Cardiac",
    note: "Unidirectional laminar canopy, copper-bonded antistatic flooring, integrated boom pendants.",
    image: OtSuit1,
  },
  {
    name: "Modular Orthopedic OT with HEPA Ceiling",
    zone: "ISO 6",
    size: "28 sq.m",
    ach: "25 ACH",
    tag: "Orthopedic",
    note: "Pressure-cascade anteroom, scrub station with elbow taps, glass-fibre wall panels.",
    image: OtSuit2,
  },
  {
    name: "Hybrid Cath-Lab OT Suite",
    zone: "ISO 5",
    size: "45 sq.m",
    ach: "20 ACH",
    tag: "Hybrid",
    note: "Ceiling-mounted C-arm rail, radiation-shielded walls, dual control room glazing.",
    image: OtSuit3,
  },
  {
    name: "Compact Day-Care Surgery Module",
    zone: "ISO 7",
    size: "20 sq.m",
    ach: "15 ACH",
    tag: "Day-Care",
    note: "Pre-fab sandwich panels, fast 6-week turnkey install, single-pass HVAC duct.",
    image: OtSuit4,
  },
  {
    name: "Neonatal & Pediatric OT Suite",
    zone: "ISO 6",
    size: "24 sq.m",
    ach: "20 ACH",
    tag: "Pediatric",
    note: "Warm-tone LED ceiling, low-noise AHU, dedicated isolette transfer bay.",
    image: OtSuit5,
  },
  {
    name: "Multi-Specialty Modular OT Block",
    zone: "ISO 5–7",
    size: "120 sq.m",
    ach: "20–25 ACH",
    tag: "Multi-OT",
    note: "Four-theatre cluster on a shared sterile corridor with central CSSD link.",
    image: OtSuit6,
  },
  {
    name: "Neuro OT with Intraoperative MRI Bay",
    zone: "ISO 5",
    size: "50 sq.m",
    ach: "20 ACH",
    tag: "Neuro",
    note: "RF-shielded enclosure, vibration-isolated flooring, dedicated cryogen vent line.",
    image: OtSuit7,
  },
  {
    name: "General Surgery OT with Modular Booms",
    zone: "ISO 6",
    size: "30 sq.m",
    ach: "20 ACH",
    tag: "General",
    note: "Dual ceiling-mounted supply units, antimicrobial vinyl flooring, LED surgical lighting.",
    image: OtSuit8,
  },
  {
    name: "ICU-Linked Trauma OT Suite",
    zone: "ISO 5",
    size: "36 sq.m",
    ach: "25 ACH",
    tag: "Trauma",
    note: "Direct ICU transfer corridor, rapid-access crash cart bay, redundant medical gas manifold.",
    image: OtSuit9,
  },
];

function SpecLine({ zone, size, ach }) {
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11.5,
        letterSpacing: 0.3,
        color: tokens.sub,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>ZONE&nbsp;<b style={{ color: tokens.teal }}>{zone}</b></span>
      <span>·</span>
      <span>SIZE&nbsp;<b style={{ color: tokens.ink }}>{size}</b></span>
      <span>·</span>
      <span>{ach}</span>
    </div>
  );
}

function SuiteCard({ s, onGetQuote }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: tokens.card,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${tokens.hairline}`,
        boxShadow: hover
          ? "0 14px 28px -14px rgba(14,124,123,0.35)"
          : "0 2px 6px -2px rgba(43,58,66,0.08)",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      {/* corner bracket signature */}
      <div
        style={{
          position: "absolute", top: 10, left: 10, width: 22, height: 22,
          borderTop: `2px solid ${tokens.teal}`, borderLeft: `2px solid ${tokens.teal}`,
          zIndex: 2,
        }}
      />
      <div style={{ height: 168, position: "relative", background: tokens.tealDark }}>
        {s.image ? (
          <img
            src={s.image}
            alt={s.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.55)",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: 0.5,
            }}
          >
            ADD IMAGE
          </div>
        )}
        {/* tag chip */}
        <span
          style={{
            position: "absolute", bottom: 10, right: 12,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            color: "#fff", background: "rgba(0,0,0,0.45)",
            padding: "2px 8px", borderRadius: 2,
          }}
        >
          {s.tag.toUpperCase()}
        </span>
      </div>
      <div style={{ padding: "16px 16px 18px" }}>
        <h3
          style={{
            fontFamily: "'Montserrat', 'Inter', sans-serif", fontWeight: 600, fontSize: 17.5,
            color: tokens.ink, margin: "0 0 6px", lineHeight: 1.25,
          }}
        >
          {s.name}
        </h3>
        <p style={{ fontSize: 13, color: tokens.sub, margin: "0 0 10px", lineHeight: 1.45 }}>
          {s.note}
        </p>
        <SpecLine zone={s.zone} size={s.size} ach={s.ach} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => onGetQuote(s)}
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: tokens.amber, color: tokens.ink,
              border: "none", borderRadius: 3, cursor: "pointer",
            }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OTSuiteDesigns() {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedSuite, setSelectedSuite] = useState(null);

  const handleGetQuote = (suite) => {
    setSelectedSuite(suite);
    setShowConsultation(true);
  };

  return (
     <>
    <SEO/>
    <div
      style={{
        background: tokens.bg,
        color: tokens.ink,
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`@import url('${FONT_IMPORT_URL}');
        * { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        button:focus-visible, a:focus-visible { outline: 2px solid ${tokens.teal}; outline-offset: 2px; }
        .ot-grid-3x3 { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .ot-grid-3x3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .ot-grid-3x3 { grid-template-columns: 1fr; } }
        .ot-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .ot-modal-inner {
          position: relative; width: 100%; max-width: 680px;
          max-height: 90vh; overflow-y: auto; border-radius: 18px;
        }
        .ot-modal-close {
          position: absolute; top: 14px; right: 16px; z-index: 10;
          background: #262E63; border: none; color: #fff;
          width: 32px; height: 32px; border-radius: 50%;
          font-size: 20px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          line-height: 1;
        }
      `}</style>

      {/* spacer so the fixed/sticky page navbar in the host app doesn't overlap the grid */}
      <div style={{ height: 32 }} />

      {/* GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 10px" }}>
        <div
          className="ot-grid-3x3"
          style={{
            display: "grid",
            gap: 22,
            marginTop: 70,
          }}
        >
          {suites.map((s, i) => (
            <SuiteCard key={i} s={s} onGetQuote={handleGetQuote} />
          ))}
        </div>
      </section>

      {/* Consultation Modal — opens on "Get Free Quote" click */}
      {showConsultation && (
        <div
          className="ot-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="ot-modal-inner">
            <button
              className="ot-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm suite={selectedSuite} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}